/* eslint-disable react/require-default-props */
import Tooltip from '@mui/material/Tooltip';
import { DateRangePickerDay, DateRangePickerDayProps } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { memo } from 'react';
import { GlobalFilterBarDateInput } from '../types';

export interface ModifiedDateRangeDayFilterProps {
  modifiedDays?: Set<string>;
  tooltipText?: string;
  useModified?: boolean;
  loading?: boolean;
  resetDefaultValue?: GlobalFilterBarDateInput;
}

/**
 * @param modifiedDays Dates that should appear disabled on the calendar filter (format YYYY-MM-DD)
 * @param tooltipText Optional translated text to display on hover of disabled day
 * @returns Modified date range day component that adds optional tooltip and disabled behavior
 * Note: Default props not supported with memoized component.
 */
export interface ModifiedDateRangeDayProps extends DateRangePickerDayProps<Dayjs>, ModifiedDateRangeDayFilterProps {}

// Day picker component that displays an optional badge and tooltip on the date
const ModifiedDateRangeDay = memo((props: ModifiedDateRangeDayProps) => {
  const { modifiedDays = new Set(), day, tooltipText, ...restDayProps } = props;
  const today = dayjs();

  const notDisabled = modifiedDays.has(day.format('YYYY-MM-DD'));
  const showTooltip = !notDisabled && !day.isAfter(today);

  return (
    <Tooltip
      arrow
      title={showTooltip ? tooltipText : ''}
      placement='bottom'
      data-testid={`date-range-tooltip-${day.format('YYYY-MM-DD')}`}
      slotProps={{
        popper: { modifiers: [{ name: 'offset', options: { offset: [0, -12] } }] },
        tooltip: {
          sx: {
            fontSize: '10px',
            textAlign: 'center',
          },
        },
      }}
    >
      <span>
        <DateRangePickerDay {...restDayProps} day={day} disabled={!notDisabled || modifiedDays.size === 0} />
      </span>
    </Tooltip>
  );
});

export default ModifiedDateRangeDay;
